import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { getEnrichmentFailure, getEnrichmentSuggestion, hasAnyActionError } from 'hooks/useGruAgent/helpers';
import { ActionResultWithIds, Workflow } from 'hooks/useGruAgent/types';
import { MessageV2DTO } from 'support/types';

import { useAutoScroll } from './hooks/useAutoScroll';
import { getStatusMessages, hasCompletedSteps, hasTerminalResult, isWorkflowActive } from './agentViewState';
import { AgentActionErrorContent } from './components/AgentActionErrorContent/AgentActionErrorContent';
import { AgentEnrichmentFailureContent } from './components/AgentEnrichmentFailureContent/AgentEnrichmentFailureContent';
import { AgentLoadingState } from './components/AgentLoadingState/AgentLoadingState';
import { AgentSuggestionContent } from './components/AgentSuggestionContent/AgentSuggestionContent';

export type AgentsTabContentViewProps = {
  workflow: Workflow;
  actionResults: Array<ActionResultWithIds>;
  message: MessageV2DTO;
  onRetry: () => void;
};

export const AgentsTabContentView = ({ workflow, actionResults, message, onRetry }: AgentsTabContentViewProps) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  useAutoScroll(bottomRef, actionResults.length);

  return (
    <div className="px-0! *:first:pt-0">
      <AgentsTabContent workflow={workflow} actionResults={actionResults} message={message} onRetry={onRetry} />
      <div ref={bottomRef} />
    </div>
  );
};

const AgentsTabContent = ({ workflow, actionResults, message, onRetry }: AgentsTabContentViewProps) => {
  const { t } = useTranslation();
  const isActive = isWorkflowActive(workflow);
  if (isActive) {
    const statusMessages = getStatusMessages(actionResults, t);
    return <AgentLoadingState statusMessages={statusMessages} />;
  }

  const suggestion = getEnrichmentSuggestion(actionResults);
  if (suggestion) {
    return (
      <div className="flex flex-col gap-3 p-3">
        <AgentSuggestionContent
          actionResults={actionResults}
          agentProposal={suggestion}
          canonical={message.canonical}
          messageId={message.id}
        />
      </div>
    );
  }

  if (hasAnyActionError(actionResults)) {
    return <AgentActionErrorContent onRetry={onRetry} />;
  }

  if (getEnrichmentFailure(actionResults)) {
    return <AgentEnrichmentFailureContent />;
  }

  if (hasCompletedSteps(actionResults) && !hasTerminalResult(actionResults)) {
    return (
      <AgentEnrichmentFailureContent
        description={t('webedi:sidebar.agents.agentStoppedUnexpectedly')}
        onRetry={onRetry}
      />
    );
  }

  return <AgentLoadingState statusMessages={[]} />;
};
